import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import TimelineIcon from '@mui/icons-material/Timeline';
import GetAppIcon from '@mui/icons-material/GetApp';
import DownloadIcon from '@mui/icons-material/Download';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { t } from '../../../utils/textUtils';
import { formatStatus } from './utils/formatters';
import { HERO_GRADIENT, HERO_DECORATION, getStatusStyle } from './utils/theme';

const ProjectHeader = ({
  project,
  permissions,
  onEdit,
  onViewGantt,
  onDownloadAttachments,
  onDownloadSubcon,
  onChangeStatus
}) => {
  const statusStyle = getStatusStyle(project.status);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.4)' }} />}
        aria-label="breadcrumb"
        sx={{ mb: 1.5, px: 0.5 }}
      >
        <Link
          to="/dashboard"
          style={{
            textDecoration: 'none',
            color: '#64748b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 13,
            fontWeight: 500
          }}
        >
          <HomeIcon sx={{ fontSize: 14 }} />
          {t('DASBOR')}
        </Link>
        <Link
          to="/projects"
          style={{
            textDecoration: 'none',
            color: '#64748b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 13,
            fontWeight: 500
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 14 }} />
          {t('PROYEK')}
        </Link>
        <Typography color="text.primary" sx={{ fontSize: 13, fontWeight: 600 }}>
          {project.name}
        </Typography>
      </Breadcrumbs>

      {/* Hero gradient card */}
      <Box
        sx={{
          background: HERO_GRADIENT,
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 10px 40px rgba(15, 23, 42, 0.18)',
          ...HERO_DECORATION
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 3.5 }, position: 'relative', zIndex: 1 }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
          >
            {/* Left: identity */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  label={formatStatus(project.status)}
                  sx={{
                    bgcolor: statusStyle.bg,
                    color: statusStyle.color,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 22,
                    letterSpacing: 0.3,
                    '& .MuiChip-label': { px: 1.2 }
                  }}
                  icon={
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: statusStyle.dot,
                        ml: '6px !important'
                      }}
                    />
                  }
                />
                {project.kodeProyek && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontWeight: 600,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem'
                    }}
                  >
                    {project.kodeProyek}
                  </Typography>
                )}
              </Stack>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#fff',
                  lineHeight: 1.2,
                  mb: 1.5,
                  fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2rem' }
                }}
              >
                {project.name}
              </Typography>

              {/* Meta row */}
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}
                  >
                    {project.location || t('LOKASI TIDAK TERSEDIA')}
                  </Typography>
                  {project.latitude && project.longitude && (
                    <Tooltip title={t('LIHAT DI GOOGLE MAPS')} arrow>
                      <IconButton
                        size="small"
                        component="a"
                        href={`https://www.google.com/maps?q=${project.latitude},${project.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>

                {project.jenisProyek && (
                  <Chip
                    size="small"
                    label={project.jenisProyek}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                      border: '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(4px)'
                    }}
                  />
                )}
                {project.sektor && (
                  <Chip
                    size="small"
                    label={project.sektor}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                      border: '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(4px)'
                    }}
                  />
                )}
              </Stack>
            </Box>

            {/* Right: actions */}
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ rowGap: 1, flexShrink: 0 }}
            >
              {permissions.canViewGantt && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<TimelineIcon />}
                  onClick={onViewGantt}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', boxShadow: 'none' },
                    textTransform: 'none',
                    fontSize: '0.8rem'
                  }}
                >
                  {t('BAGAN GANTT')}
                </Button>
              )}
              {permissions.canEdit && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={onDownloadAttachments}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', boxShadow: 'none' },
                    textTransform: 'none',
                    fontSize: '0.8rem'
                  }}
                >
                  {t('DOWNLOAD ATTACHMENTS')}
                </Button>
              )}
              {permissions.canDownloadSubconOpname && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={onDownloadSubcon}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', boxShadow: 'none' },
                    textTransform: 'none',
                    fontSize: '0.8rem'
                  }}
                >
                  {t('DOWNLOAD SUBCON')}
                </Button>
              )}
              {permissions.canEdit && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ChangeCircleIcon />}
                  onClick={onChangeStatus}
                  sx={{
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                    textTransform: 'none',
                    fontSize: '0.8rem'
                  }}
                >
                  {t('UBAH STATUS')}
                </Button>
              )}
              {permissions.canEdit && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={onEdit}
                  sx={{
                    bgcolor: '#3B82F6',
                    color: '#fff',
                    fontWeight: 700,
                    px: 2,
                    boxShadow: '0 4px 14px rgba(59,130,246,0.4)',
                    '&:hover': { bgcolor: '#2563EB' },
                    textTransform: 'none',
                    fontSize: '0.8rem'
                  }}
                >
                  {t('EDIT PROYEK')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectHeader;
